import type { Mark } from '../../api';

/** Artchipsen i listvyn (designens ordning). "Fågel" matchar alla fågelarter. */
export const SPECIES_CHIPS = ['Älg', 'Kronhjort', 'Dovhjort', 'Rådjur', 'Vildsvin', 'Björn', 'Fågel'] as const;

const BIRDS = ['fågel', 'fasan', 'and', 'gräsand', 'kricka', 'knipa', 'gås', 'kanadagås', 'grågås', 'orre', 'tjäder', 'järpe', 'ripa', 'rapphöna', 'duva', 'ringduva', 'skogsfågel', 'sjöfågel', 'andfågel'];

const SPECIES_NAMES: Record<string, string> = {
  'älg': 'Älg', 'dovhjort': 'Dovhjort', 'kronhjort': 'Kronhjort', 'rådjur': 'Rådjur', 'råbock': 'Råbock', 'vildsvin': 'Vildsvin',
  'fasan': 'Fasan', 'björn': 'Björn', 'lo': 'Lo', 'räv': 'Räv', 'hare': 'Hare', 'and': 'And', 'kanadagås': 'Kanadagås',
  'grågås': 'Grågås', 'kricka': 'Kricka', 'knipa': 'Knipa', 'mink': 'Mink', 'utter': 'Utter', 'grävling': 'Grävling',
};
export const speciesName = (k: string) => SPECIES_NAMES[k.toLowerCase()] ?? (k.charAt(0).toUpperCase() + k.slice(1));

export const LEVEL_TEXT = ['', 'Sällsynt', 'Sparsam', 'Förekommande', 'Riklig', 'Mycket riklig'];

const FACILITY_NAMES: Record<string, string> = {
  slaktrum: 'Slaktrum', kylrum: 'Kylrum', eldstad: 'Eldstad', atlar: 'Åtel', viltkameror: 'Viltkameror', jaktkojor: 'Jaktkoja',
  pass_torn: 'Jakttorn', pass_stol: 'Pass med stol', markpass: 'Markpass', overnattning: 'Övernattning', bastu: 'Bastu', badtunna: 'Badtunna',
};

function parseJson<T>(raw: string | undefined | null, fallback: T): T {
  if (!raw) return fallback;
  try { return JSON.parse(raw) as T; } catch { return fallback; }
}

export const gameTypes = (m: Mark): string[] => parseJson<string[]>(m.gameTypesJson, []);
export const highlights = (m: Mark): string[] => parseJson<string[]>(m.highlightsJson, []);
export const includes = (m: Mark): string[] => parseJson<string[]>(m.includesJson, []);

/** Viltförekomst 1–5, sorterad med rikligast först. */
export function occurrence(m: Mark): { key: string; name: string; level: number }[] {
  const o = parseJson<Record<string, number>>(m.speciesOccurrenceJson, {});
  return Object.entries(o).filter(([, l]) => l > 0).map(([k, l]) => ({ key: k, name: speciesName(k), level: Math.min(5, l) })).sort((a, b) => b.level - a.level);
}

export function facilities(m: Mark): { key: string; name: string; count: number }[] {
  const f = parseJson<Record<string, number>>(m.facilitiesJson, {});
  return Object.entries(f).filter(([, v]) => v > 0).map(([k, v]) => ({ key: k, name: FACILITY_NAMES[k] ?? k, count: v }));
}

/** Antal pass/torn på marken (summa av passtyperna). */
export function standCount(m: Mark): number {
  const f = parseJson<Record<string, number>>(m.facilitiesJson, {});
  return (f.pass_torn || 0) + (f.pass_stol || 0) + (f.markpass || 0);
}

/** Säsongspris (årsavgift) och dagpris (gästkostnad per jakt) om de finns. */
export function markPrices(m: Mark): { season?: number; day?: number } {
  const cm = m.costModel;
  const season = cm?.arsavgift && cm.arsavgift > 0 ? cm.arsavgift : (m.pricePerYear || undefined);
  const day = cm?.gastKostnadPerJakt && cm.gastKostnadPerJakt > 0 ? cm.gastKostnadPerJakt : undefined;
  return { season, day };
}

export const kr = (n: number) => `${n.toLocaleString('sv-SE')} kr`;

/** Kortets prisrad: dagpris ("från 4 500 kr / dag") före säsongspris ("18 000 kr / säsong"). */
export function priceLabel(m: Mark): { price: string; unit: string; sort: number } | null {
  const { season, day } = markPrices(m);
  if (day) return { price: `från ${kr(day)}`, unit: '/ dag', sort: day };
  if (season) return { price: kr(season), unit: '/ säsong', sort: season };
  return null;
}

/** OR-logik: marken matchar om någon av de valda arterna finns i arterna eller förekomsten. */
export function matchesSpecies(m: Mark, active: string[]): boolean {
  if (!active.length) return true;
  const all = [...gameTypes(m), ...occurrence(m).map(o => o.key)].map(s => s.toLowerCase());
  return active.some(a => a === 'Fågel'
    ? all.some(s => BIRDS.some(b => s.includes(b)))
    : all.some(s => s.includes(a.toLowerCase())));
}

export function placeLine(m: Mark): string {
  if (m.municipality && m.county) return `${m.municipality} · ${m.county}`;
  return m.municipality || m.county || 'Okänd plats';
}

const IMPORT_LABELS: Record<string, string> = { minjaktlagsplats: 'minjaktlagsplats.se' };

/** IM1: importerad mark – källans namn och länk, annars null. */
export function importInfo(m: Pick<Mark, 'importSource' | 'importSourceUrl'>): { label: string; url: string | null } | null {
  if (!m.importSource) return null;
  const url = m.importSourceUrl && /^https:\/\//.test(m.importSourceUrl) ? m.importSourceUrl : null;
  return { label: IMPORT_LABELS[m.importSource] ?? m.importSource, url };
}

const slugify = (s: string) => s.toLowerCase()
  .replace(/[åä]/g, 'a').replace(/ö/g, 'o').replace(/é/g, 'e')
  .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'jaktmark';

/** `/jakt/:slug` — namnet plus början av id:t, så att slugen är läsbar men unik. */
export const markSlug = (m: Mark) => `${slugify(m.name)}-${m.id.replace(/-/g, '').slice(0, 6)}`;
export const markPath = (m: Mark) => `/jakt/${markSlug(m)}`;
export function slugFromPath(path: string): string | null {
  const m = path.match(/^\/jakt\/([^/?#]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

/** Nuvarande jaktsäsong, t.ex. "2026/27" (säsongen börjar 1 juli). */
export function currentSeason(d = new Date()): string {
  const y = d.getMonth() >= 6 ? d.getFullYear() : d.getFullYear() - 1;
  return `${y}/${String(y + 1).slice(2)}`;
}

/** Jaktåret (1 juli–30 juni) som annonsens datum ligger i, t.ex. "2026/27" eller "2026/27–2027/28". Null utan datum. */
export function seasonForDates(dates: { start: string; end?: string | null }[]): string | null {
  const days = dates.flatMap(d => [d.start, d.end || d.start]).filter(Boolean).sort();
  if (!days.length) return null;
  const first = currentSeason(new Date(`${days[0].slice(0, 10)}T12:00:00`));
  const last = currentSeason(new Date(`${days[days.length - 1].slice(0, 10)}T12:00:00`));
  return first === last ? first : `${first}–${last}`;
}

/** Sparade favoriter — lokalt per enhet tills det finns ett API för det. */
const FAV_KEY = 'qvenari_favs';
export function loadFavs(): Set<string> {
  try { return new Set(JSON.parse(localStorage.getItem(FAV_KEY) || '[]')); } catch { return new Set(); }
}
export function saveFavs(f: Set<string>) {
  try { localStorage.setItem(FAV_KEY, JSON.stringify([...f])); } catch { /* privat läge */ }
}
